/**
 * useVoiceStream.js — full-duplex streaming voice pipeline (React hook).
 *
 * Owns the browser side of `/api/voice/stream`:
 *   • Captures mic audio via AudioWorklet → PCM16 mono 16 kHz.
 *   • Streams frames over WebSocket to the backend.
 *   • Receives streaming assistant transcript + raw PCM audio frames.
 *   • Plays PCM audio through Web Audio API with tightly scheduled buffers so
 *     the first audible byte lands < ~200 ms after arrival.
 *   • Exposes a barge-in API: on server "speech_started" event we tear down
 *     scheduled audio immediately, and the user hears themselves clearly.
 *
 * Consumer contract:
 *   const { state, level, partial, finalText, assistantText, latency, start,
 *           stop, setFocusMode, setVoice }
 *     = useVoiceStream({ voice, conversationId, focusMode, onTurnDone });
 *
 * `state` ∈ "idle" | "connecting" | "listening" | "thinking" | "speaking" | "error"
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
// A turn that produces nothing in this window is dead — hand the mic back.
const TURN_WATCHDOG_MS = 15000;
// How long we'll wait for the browser's microphone prompt to be answered.
const MIC_PROMPT_TIMEOUT_MS = 20000;

/** Tell the server why voice failed. "Voice pipeline failed" tells nobody
 *  anything — least of all us, when a user reports it. Fire and forget. */
function reportVoiceError(code, detail) {
  try {
    fetch(`${BACKEND}/api/voice/client-error`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        code,
        detail: String(detail || "").slice(0, 400),
        ua: navigator.userAgent.slice(0, 200),
      }),
      credentials: "include",
      keepalive: true,
    }).catch(() => {});
  } catch (_) { /* diagnostics must never break the thing they diagnose */ }
}

function buildWsUrl({ voice, conversationId, focusMode }) {
  // BACKEND is https://... — convert scheme to wss://
  const httpBase = BACKEND || window.location.origin;
  const wsBase = httpBase.replace(/^http/, "ws");
  const params = new URLSearchParams();
  if (voice) params.set("voice", voice);
  if (conversationId) params.set("conversation_id", conversationId);
  if (focusMode) params.set("focus_mode", focusMode);
  // The browser is the only thing that knows where this person is.
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (tz) params.set("tz", tz);
  } catch (_) { /* ignore */ }
  return `${wsBase}/api/voice/stream?${params.toString()}`;
}

async function fetchAuthToken() {
  // We authenticate the WS via a short-lived token exposed by /api/auth/ws-token.
  // If that endpoint doesn't exist yet, fall back to the raw cookie flow (WS
  // upgrade requests send cookies from same-origin). The server accepts either.
  try {
    const r = await fetch(`${BACKEND}/api/auth/ws-token`, {
      method: "POST",
      credentials: "include",
    });
    if (r.ok) {
      const j = await r.json();
      return j.token || "";
    }
  } catch (_) { /* ignore */ }
  return "";
}

export function useVoiceStream({
  voice = "hale",
  conversationId = null,
  focusMode = null,
  anonSessionId = null,   // marketing page: no account, same pipeline
  onTurnDone,   // (fullText, latency) => void
  onNewConversation, // (conversation_id) => void
  onMyVoiceInvite,   // (msg) => void — server thinks the user is engaged
                     // enough to benefit from the formal My Voice enrollment
} = {}) {
  const [state, setState] = useState("idle");
  const [level, setLevel] = useState(0);      // 0..1 mic RMS for the visualizer
  const [partial, setPartial] = useState(""); // live interim transcript
  const [finalText, setFinalText] = useState("");
  const [assistantText, setAssistantText] = useState("");
  const [latency, setLatency] = useState(null); // last turn's per-stage timing
  // Transparency-badge state — mirrors what text chat gets via SSE `meta.credibility`
  // and `event: sources`. Reset on every new user turn so the badge tracks the
  // reply currently in flight rather than the previous one.
  const [credibility, setCredibility] = useState(null);
  const [sources, setSources] = useState([]);
  const [progress, setProgress] = useState(null); // {stage, label} — research narration
  const [narration, setNarration] = useState("");  // what Rammy just said it's doing
  const narrationRef = useRef("");
  narrationRef.current = narration;
  const [errorCode, setErrorCode] = useState(null); // e.g. "insufficient_credits"
  const errorCodeRef = useRef(null);
  errorCodeRef.current = errorCode;
  const [creditsPerMinute, setCreditsPerMinute] = useState(null);

  const wsRef = useRef(null);
  // Turn watchdog — a reply that never arrives must hand the mic back rather
  // than spin. This is the thing that used to leave the ring turning forever.
  const turnWatchdogRef = useRef(0);
  const clearTurnWatchdog = useCallback(() => {
    if (turnWatchdogRef.current) {
      clearTimeout(turnWatchdogRef.current);
      turnWatchdogRef.current = 0;
    }
  }, []);
  const armTurnWatchdog = useCallback(() => {
    clearTurnWatchdog();
    turnWatchdogRef.current = setTimeout(() => {
      turnWatchdogRef.current = 0;
      if (stoppedRef.current) return;
      console.warn("voice turn watchdog fired");
      toast.error("I lost that one — say it again?");
      setPartial("");
      setState("listening");
    }, TURN_WATCHDOG_MS);
  }, [clearTurnWatchdog]);
  const audioCtxRef = useRef(null);
  const workletRef = useRef(null);
  const micStreamRef = useRef(null);
  const playCtxRef = useRef(null);              // separate ctx for playback timing
  const nextPlayAtRef = useRef(0);              // when the next PCM chunk should start
  const scheduledSourcesRef = useRef([]);       // AudioBufferSourceNodes to allow barge-in stop
  const stoppedRef = useRef(false);
  const speakingTimerRef = useRef(0);          // un-ducks the capture gate
  const openedRef = useRef(false);              // did the socket ever open?
  // Every start() gets a generation number. React StrictMode (and any fast
  // open/close) runs the effect twice, and the first run's awaits land AFTER
  // the second run has begun — which used to leave two sockets fighting, one
  // orphaned, and the UI stuck on "waking up" for ever.
  const runIdRef = useRef(0);
  const assistantBufRef = useRef("");           // accumulator for the live caption

  // ─── Mic setup + WS wiring ────────────────────────────────────────────────
  const start = useCallback(async () => {
    if (wsRef.current) return; // already running
    const run = ++runIdRef.current;
    const superseded = () => run !== runIdRef.current || stoppedRef.current;
    stoppedRef.current = false;
    openedRef.current = false;
    setState("connecting");
    setErrorCode(null);
    setPartial("");
    setFinalText("");
    setAssistantText("");
    assistantBufRef.current = "";

    // 1. Grab mic. If the permission prompt is never answered, getUserMedia
    // just… waits, and the ring spins for ever. Give it a deadline.
    let stream;
    try {
      stream = await Promise.race([
        navigator.mediaDevices.getUserMedia({
          audio: {
            channelCount: 1,
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true,
          },
        }),
        new Promise((_, reject) => setTimeout(
          () => reject(Object.assign(new Error("mic permission not answered"),
                                     { name: "TimeoutError" })),
          MIC_PROMPT_TIMEOUT_MS,
        )),
      ]);
    } catch (e) {
      // Be specific: a blocked mic and a missing mic need different advice,
      // and neither of them is "the pipeline failed".
      const name = e?.name || "";
      const code = name === "NotAllowedError" || name === "SecurityError"
        ? "mic_denied"
        : (name === "NotFoundError" || name === "OverconstrainedError")
          ? "mic_missing"
          : name === "TimeoutError"
            ? "mic_pending"
            : "mic_failed";
      console.warn("mic error:", name, e);
      setErrorCode(code);
      reportVoiceError(code, `${name}: ${e?.message || ""}`);
      setState("error");
      return;
    }
    if (superseded()) {
      stream.getTracks().forEach((t) => t.stop());
      return;
    }
    micStreamRef.current = stream;

    // 2. Capture AudioContext + worklet.
    const AC = window.AudioContext || window.webkitAudioContext;
    const ctx = new AC();
    audioCtxRef.current = ctx;
    try {
      await ctx.audioWorklet.addModule("/pcm16-processor.js");
    } catch (e) {
      console.warn("worklet load failed:", e);
      setErrorCode("audio_engine_failed");
      reportVoiceError("audio_engine_failed", e?.message);
      setState("error");
      return;
    }
    if (superseded()) {
      try { await ctx.close(); } catch (_) { /* ignore */ }
      stream.getTracks().forEach((t) => t.stop());
      return;
    }
    const source = ctx.createMediaStreamSource(stream);
    const node = new AudioWorkletNode(ctx, "pcm16-processor");
    workletRef.current = node;

    // 3. Playback AudioContext — kept separate so playback latency doesn't
    //    get penalised by any capture-side scheduling hiccups.
    playCtxRef.current = new AC({ sampleRate: 16000 });
    nextPlayAtRef.current = 0;

    // 4. Open WS.
    // A visitor has no account to mint a token for — the server accepts an
    // anon session id instead and puts the free message limit behind it.
    const token = anonSessionId ? "" : await fetchAuthToken();
    if (!token && !anonSessionId) {
      console.warn("no ws token — is /api/auth/ws-token available?");
      setErrorCode("auth_expired");
      reportVoiceError("auth_expired", "ws-token returned nothing");
      setState("error");
      return;
    }
    if (superseded()) {
      try { await ctx.close(); } catch (_) { /* ignore */ }
      stream.getTracks().forEach((t) => t.stop());
      return;
    }
    const url = anonSessionId
      ? `${buildWsUrl({ voice, conversationId, focusMode })}&anon_session=${encodeURIComponent(anonSessionId)}`
      : `${buildWsUrl({ voice, conversationId, focusMode })}&token=${encodeURIComponent(token)}`;
    const ws = new WebSocket(url);
    ws.binaryType = "arraybuffer";
    wsRef.current = ws;

    ws.onopen = () => {
      if (superseded()) { ws.close(); return; }
      openedRef.current = true;
      setState("listening");
      // Wire mic → WS.
      // Wire mic → WS. The worklet only hands us audio when its noise gate
      // is open, so a windy street never reaches the recogniser.
      node.port.onmessage = (e) => {
        const { audio, rms, silence } = e.data || {};
        if (audio && ws.readyState === WebSocket.OPEN) {
          ws.send(audio);
        }
        if (silence && ws.readyState === WebSocket.OPEN) {
          // Nothing above the room's noise floor for 10 s. Let the server
          // check in out loud instead of sitting there looking alive.
          ws.send(JSON.stringify({ op: "no_speech" }));
        }
        if (typeof rms === "number") {
          setLevel(Math.min(1, rms * 8));
        }
      };
      // Silent gain sink so the graph runs but the mic isn't looped back.
      const sink = ctx.createGain();
      sink.gain.value = 0;
      source.connect(node).connect(sink).connect(ctx.destination);
    };

    ws.onmessage = (ev) => {
      if (run !== runIdRef.current) return;
      if (ev.data instanceof ArrayBuffer) {
        // PCM 16 kHz mono → schedule playback.
        _playPcm(ev.data);
        if (state !== "speaking") setState("speaking");
        return;
      }
      // JSON control frame.
      let msg;
      try { msg = JSON.parse(ev.data); } catch (_) { return; }
      switch (msg.type) {
        case "speech_started":
          // Raw voice-activity detection — energy, not words. This is NOT an
          // interruption: wind, a fan, or Rammy's own voice in the mic all
          // trip it, and tearing playback down here is what left people with
          // "Let me look that up" and then silence. The server decides what
          // counts as a real interruption and sends `cancel`.
          break;
        case "partial":
          setPartial(msg.text || "");
          break;
        case "final":
          // Deepgram is-final chunk — build the utterance as it comes in.
          setPartial(msg.text || "");
          break;
        case "user_utterance":
          setFinalText(msg.text || "");
          setNarration("");
          setPartial("");
          assistantBufRef.current = "";
          setAssistantText("");
          setState("thinking");
          armTurnWatchdog();
          // Fresh user turn → reset transparency state so the badge tracks
          // the reply currently being composed, not the previous one.
          setCredibility(null);
          setSources([]);
          setProgress(null);
          break;
        case "assistant_delta":
          clearTurnWatchdog();
          if (narrationRef.current) setNarration("");
          assistantBufRef.current += (msg.content || "");
          setAssistantText(assistantBufRef.current);
          if (state !== "speaking") setState("speaking");
          break;
        case "meta":
          if (msg.conversation_id && onNewConversation) {
            onNewConversation(msg.conversation_id);
          }
          break;
        case "credibility":
          // Per-turn credibility summary from chat_router → renders the
          // transparency pill under the reply. Same shape text chat uses.
          if (msg.credibility) setCredibility(msg.credibility);
          break;
        case "sources":
          if (Array.isArray(msg.sources)) setSources(msg.sources);
          break;
        case "progress":
          // Research narration ("Checked TMDB · composing…"). Kept as
          // {stage, label} so the UI can decide whether to show it.
          setProgress({ stage: msg.stage || "", label: msg.label || "" });
          break;
        case "turn_done":
          clearTurnWatchdog();
          if (msg.conversation_id && onNewConversation) {
            onNewConversation(msg.conversation_id);
          }
          if (msg.latency_ms) setLatency(msg.latency_ms);
          if (onTurnDone) onTurnDone(msg.text || "", msg.latency_ms || null);
          // Reset for the next user turn once the audio queue drains.
          _onQueueDrained(() => {
            if (stoppedRef.current) return;
            setState("listening");
          });
          break;
        case "cancel":
          clearTurnWatchdog();
          _stopPlayback();
          setState("listening");
          break;
        case "myvoice_invite":
          if (onMyVoiceInvite) onMyVoiceInvite(msg);
          break;
        case "narration":
          // "Checking the weather." — spoken and shown.
          setNarration(msg.text || "");
          break;
        case "credits":
          if (msg.per_minute) setCreditsPerMinute(msg.per_minute);
          if (msg.minutes_billed) window.dispatchEvent(new Event("rammy-credits-changed"));
          break;
        case "error":
          console.warn("voice-stream error:", msg.message);
          setErrorCode(msg.code || null);
          if (msg.code === "anon_limit") {
            toast.error(msg.message || "That's the free trial done.");
          }
          if (msg.code === "insufficient_credits") {
            window.dispatchEvent(new Event("rammy-credits-changed"));
            toast.error(msg.message || "You're out of credits.", {
              action: {
                label: "Top up",
                onClick: () => window.dispatchEvent(
                  new CustomEvent("rammy-navigate", { detail: { target: "credits" } })),
              },
            });
          }
          setState("error");
          break;
        default:
          break;
      }
    };

    ws.onclose = (ev) => {
      if (run !== runIdRef.current || stoppedRef.current) return;
      // Never opened → the line couldn't be established at all.
      if (!openedRef.current) {
        setErrorCode("ws_failed");
        reportVoiceError("ws_failed", `close ${ev?.code} before open`);
        setState("error");
        return;
      }
      setState("idle");
    };
    ws.onerror = () => {
      if (run !== runIdRef.current || stoppedRef.current) return;
      if (!errorCodeRef.current) {
        setErrorCode("ws_failed");
        reportVoiceError("ws_failed", "socket error");
      }
      setState("error");
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [voice, conversationId, focusMode, anonSessionId]);

  // ─── PCM playback scheduling ──────────────────────────────────────────────
  const _markSpeaking = () => {
    // Tell the capture worklet to raise its gate while Rammy talks, so her
    // own audio coming back through the mic can't interrupt her.
    const node = workletRef.current;
    if (!node) return;
    try { node.port.postMessage({ speaking: true }); } catch (_) { /* ignore */ }
    if (speakingTimerRef.current) clearTimeout(speakingTimerRef.current);
    const ctx = playCtxRef.current;
    const remaining = ctx ? Math.max(0, nextPlayAtRef.current - ctx.currentTime) : 0;
    speakingTimerRef.current = setTimeout(() => {
      speakingTimerRef.current = 0;
      try { node.port.postMessage({ speaking: false, reset: true }); } catch (_) { /* ignore */ }
    }, Math.round(remaining * 1000) + 250);
  };

  const _playPcm = (arrayBuffer) => {
    const ctx = playCtxRef.current;
    if (!ctx || arrayBuffer.byteLength === 0) return;
    // Wake the audio context if the browser suspended it (Chrome autoplay).
    if (ctx.state === "suspended") ctx.resume().catch(() => {});
    const int16 = new Int16Array(arrayBuffer);
    const float32 = new Float32Array(int16.length);
    for (let i = 0; i < int16.length; i++) float32[i] = int16[i] / 0x8000;
    const buf = ctx.createBuffer(1, float32.length, 16000);
    buf.getChannelData(0).set(float32);
    const src = ctx.createBufferSource();
    src.buffer = buf;
    src.connect(ctx.destination);
    const startAt = Math.max(ctx.currentTime, nextPlayAtRef.current);
    src.start(startAt);
    nextPlayAtRef.current = startAt + buf.duration;
    scheduledSourcesRef.current.push(src);
    src.onended = () => {
      scheduledSourcesRef.current = scheduledSourcesRef.current.filter(s => s !== src);
    };
    _markSpeaking();
  };

  const _stopPlayback = () => {
    for (const s of scheduledSourcesRef.current) {
      try { s.stop(0); } catch (_) { /* ignore */ }
    }
    scheduledSourcesRef.current = [];
    if (playCtxRef.current) {
      nextPlayAtRef.current = playCtxRef.current.currentTime;
    }
    if (speakingTimerRef.current) {
      clearTimeout(speakingTimerRef.current);
      speakingTimerRef.current = 0;
    }
    try {
      workletRef.current?.port.postMessage({ speaking: false, reset: true });
    } catch (_) { /* ignore */ }
  };

  const _onQueueDrained = (cb) => {
    const ctx = playCtxRef.current;
    if (!ctx) { cb(); return; }
    const remaining = Math.max(0, nextPlayAtRef.current - ctx.currentTime);
    setTimeout(cb, Math.round(remaining * 1000) + 50);
  };

  // ─── Public control ops ───────────────────────────────────────────────────
  const stop = useCallback(() => {
    stoppedRef.current = true;
    runIdRef.current += 1;      // anything still in flight is now an orphan
    if (turnWatchdogRef.current) { clearTimeout(turnWatchdogRef.current); turnWatchdogRef.current = 0; }
    try { wsRef.current?.close(); } catch (_) { /* ignore */ }
    wsRef.current = null;
    _stopPlayback();
    try { workletRef.current?.disconnect(); } catch (_) { /* ignore */ }
    try { micStreamRef.current?.getTracks().forEach(t => t.stop()); } catch (_) { /* ignore */ }
    try { audioCtxRef.current?.close(); } catch (_) { /* ignore */ }
    try { playCtxRef.current?.close(); } catch (_) { /* ignore */ }
    workletRef.current = null;
    micStreamRef.current = null;
    audioCtxRef.current = null;
    playCtxRef.current = null;
    setState("idle");
    setLevel(0);
  }, []);

  const setFocusModeOp = useCallback((mode) => {
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({ op: "focus_mode", mode: mode || "" }));
    }
  }, []);

  const setVoiceOp = useCallback((v) => {
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({ op: "voice", voice: v || "" }));
    }
  }, []);

  const cancel = useCallback(() => {
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({ op: "cancel" }));
    }
    _stopPlayback();
    // Barge-in must read as instant (<=120ms). Flip the local state now
    // rather than waiting for the server's next state frame.
    setState("listening");
  }, []);

  const dismissMyVoiceInvite = useCallback(() => {
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({ op: "dismiss_myvoice_invite" }));
    }
  }, []);

  useEffect(() => () => stop(), [stop]);

  return {
    state,
    level,
    partial,
    finalText,
    assistantText,
    latency,
    credibility,
    sources,
    progress,
    narration,
    errorCode,
    creditsPerMinute,
    start,
    stop,
    cancel,
    dismissMyVoiceInvite,
    setFocusMode: setFocusModeOp,
    setVoice: setVoiceOp,
  };
}
